import { Container, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { CSSObject, Theme, styled } from '@mui/material/styles';
import { Iconify } from '@sdf/base';
import { Fragment, useEffect, useMemo, useRef, useState, useContext } from 'react';
import Logo from '../../Logo';
import { sideDrawerStyle as style } from '../../../styles';
import MenuList from './MenuList';
import { useRouter } from 'next/router';
import { mergeSx, SxProp } from '@t2c/shared-fe';
import { AppContext } from '../../../context/AppContext';
import { useTour } from '@reactour/tour';

const drawerWidth = 250;

const openedMixin = (theme: Theme): CSSObject => ({
  width: '100%',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'visible',
  overflowY: 'visible',
  padding: 20,
  paddingTop: 2,
  borderRight: `2px solid ${theme.palette.background.sidebarBorder}`,
  backgroundColor: theme.palette.background.secondary,
  [theme.breakpoints.up('xs')]: {
    width: drawerWidth,
  },
  [theme.breakpoints.down('sm')]: { 
    width: "100%"
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'visible',
  overflowY: 'visible',
  padding: 4,
  paddingTop: 2,
  background: theme.palette.background.secondary,
  borderRight: `2px solid ${theme.palette.background.sidebarBorder}`,
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('xs')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  [theme.breakpoints.down('sm')]: { 
    width: 0,
    borderRight: 'none',
    padding: 0,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  backgroundColor: theme.palette.background.secondary,
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: '100%',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.secondary,

    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
    // [theme.breakpoints.up('xs')]: {
    //   width: drawerWidth,
    // },
  })
);

// ----------------------------------------------------------------------------------

interface SideDrawerProps {
  children: React.ReactNode;
  containerSxProps?: SxProp;
  childrenWrapperSx?: SxProp;
  setIsFullWdithHeader?: (_: boolean) => void;
}

export default function SideDrawer({
  children,
  containerSxProps,
  childrenWrapperSx,
  setIsFullWdithHeader,
}: SideDrawerProps) {
  const { push } = useRouter();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const appContext = useContext(AppContext);
  const isDrawerOpen = appContext?.isDrawerOpen ?? false;
  const setIsDrawerOpen = appContext?.setIsDrawerOpen;
  const { isOpen } = useTour();

  useEffect(() => {
    if (isExtraSmall) {
      setIsDrawerOpen?.(false);
    }
  }, [isExtraSmall, setIsDrawerOpen]);

  const onToggleDrawer = () => {
    if (setIsFullWdithHeader) setIsFullWdithHeader(isDrawerOpen);

    setIsDrawerOpen?.((prevState) => !prevState);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isOpen) return;
      
      if (
        isExtraSmall &&
        isDrawerOpen &&
        drawerRef.current && 
        !drawerRef.current.contains(event.target as Node)
      ) {
        onToggleDrawer();
      }
    }
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  },  [isExtraSmall, isDrawerOpen]);

  return (
    <Box sx={style.container}>
      <CssBaseline />
      <Drawer variant="permanent" open={isDrawerOpen} ref={drawerRef}>
        <DrawerHeader sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={'center'}
            sx={{ width: '100%' }}
          >
            {isDrawerOpen && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
                onClick={() => {
                  push('/dashboard');
                  isExtraSmall && onToggleDrawer();
                }}
                sx={{ cursor: 'pointer', width: '100%' }}
              >
                <Logo
                  height={isDrawerOpen ? 42 : 32}
                  width={isDrawerOpen ? 160 : 32}
                  onlyLogo={!isDrawerOpen}
                  disabledLink
                />
              </Stack>
            )}
            <IconButton 
              onClick={onToggleDrawer} 
              size="small" 
              sx={{
                ...style.drawerBtnWrapper,
                ...(isExtraSmall && !isDrawerOpen && { 
                  transform: 'translateX(20px) translateY(4px)',
                })
              }}
            >
              <Iconify
                icon={
                  isDrawerOpen ? 
                  (isExtraSmall?
                  'system-uicons:cross'
                  : 'iconamoon:arrow-left-2' )
                  : (isExtraSmall ? 
                  'material-symbols:menu-rounded'
                  : 'iconamoon:arrow-right-2')}
                sx={{
                  ...style.drawerBtn,
                  fontSize: isExtraSmall ? 26 : 20,
                }}
              />
            </IconButton>
          </Stack>
        </DrawerHeader>
        <MenuList isDrawerOpen={isDrawerOpen} onToggleDrawer={onToggleDrawer} />
      </Drawer>
      <Box component="main" sx={mergeSx(style.childrenWrapper, childrenWrapperSx)}>
        <Container maxWidth={false} sx={{ height: '100%', ...containerSxProps, pt: 0 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
}
